import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PassBarcode } from '../../types/pass';
import type { RootState } from '../store';

export type FieldLocalisations = Record<
  string,
  Record<string, string>
>; // { fieldId: { locale: value } }

export interface PassState {
  fieldValues: Record<string, string>;
  fieldLocalisations: FieldLocalisations;
  extId: string;
  groupTags: string[];
  barcode: PassBarcode;
  nfcEnabled: boolean;
  nfcSource: string;
  nfcCustomValue: string;
  expiryDate: string;
  expiryTime: string;
  expiryTimezone: string;
  templateUuid: string;
  isInitialised: boolean;
  focusedField: string | null;
}

export const initialPassState: PassState = {
  fieldValues: {},
  fieldLocalisations: {},
  extId: '',
  groupTags: [],
  barcode: {
    enabled: false,
    type: null,
    source: null,
    value: '',
    displayText: false,
  },
  nfcEnabled: false,
  nfcSource: 'uuid',
  nfcCustomValue: '',
  expiryDate: '',
  expiryTime: '',
  expiryTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  templateUuid: '',
  isInitialised: false,
  focusedField: null,
};

const slice = createSlice({
  name: 'pass',
  initialState: initialPassState,
  reducers: {
    initialiseFromPass(
      state,
      action: PayloadAction<Omit<PassState, 'isInitialised'>>
    ) {
      Object.assign(state, action.payload, { isInitialised: true });
    },

    resetPass() {
      return initialPassState;
    },

    setFieldValue(
      state,
      action: PayloadAction<{ fieldId: string; value: string }>
    ) {
      state.fieldValues[action.payload.fieldId] = action.payload.value;
    },

    setExtId(state, action: PayloadAction<string>) {
      state.extId = action.payload;
    },

    setGroupTags(state, action: PayloadAction<string[]>) {
      state.groupTags = action.payload;
    },

    setBarcode(state, action: PayloadAction<Partial<PassBarcode>>) {
      Object.assign(state.barcode, action.payload);
    },

    setNfcEnabled(state, action: PayloadAction<boolean>) {
      state.nfcEnabled = action.payload;
    },

    setNfcSource(state, action: PayloadAction<string>) {
      state.nfcSource = action.payload;
    },

    setNfcCustomValue(state, action: PayloadAction<string>) {
      state.nfcCustomValue = action.payload;
    },

    setExpiryDate(state, action: PayloadAction<string>) {
      state.expiryDate = action.payload;
    },

    setExpiryTime(state, action: PayloadAction<string>) {
      state.expiryTime = action.payload;
    },

    setExpiryTimezone(state, action: PayloadAction<string>) {
      state.expiryTimezone = action.payload;
    },

    setTemplateUuid(state, action: PayloadAction<string>) {
      state.templateUuid = action.payload;
    },

    setFieldLocalisation(
      state,
      action: PayloadAction<{ fieldId: string; locale: string; value: string }>
    ) {
      const { fieldId, locale, value } = action.payload;
      if (!state.fieldLocalisations[fieldId]) {
        state.fieldLocalisations[fieldId] = {};
      }
      state.fieldLocalisations[fieldId][locale] = value;
    },

    addFieldTranslations(
      state,
      action: PayloadAction<{ fieldId: string; locales: string[] }>
    ) {
      const { fieldId, locales } = action.payload;
      if (!state.fieldLocalisations[fieldId]) {
        state.fieldLocalisations[fieldId] = {};
      }
      locales.forEach((locale) => {
        if (state.fieldLocalisations[fieldId][locale] === undefined) {
          state.fieldLocalisations[fieldId][locale] = '';
        }
      });
    },

    removeFieldTranslations(state, action: PayloadAction<string>) {
      delete state.fieldLocalisations[action.payload];
    },

    setFocusedField(state, action: PayloadAction<string | null>) {
      state.focusedField = action.payload;
    },

    syncFromTemplate(
      state,
      action: PayloadAction<{
        barcode: PassBarcode;
        nfcEnabled: boolean;
        nfcSource: string;
        nfcCustomValue: string;
      }>
    ) {
      state.barcode = action.payload.barcode;
      state.nfcEnabled = action.payload.nfcEnabled;
      state.nfcSource = action.payload.nfcSource;
      state.nfcCustomValue = action.payload.nfcCustomValue;
    },
  },
});

export const {
  initialiseFromPass,
  resetPass,
  setFieldValue,
  setExtId,
  setGroupTags,
  setBarcode,
  setNfcEnabled,
  setNfcSource,
  setNfcCustomValue,
  setExpiryDate,
  setExpiryTime,
  setExpiryTimezone,
  setTemplateUuid,
  setFieldLocalisation,
  addFieldTranslations,
  removeFieldTranslations,
  setFocusedField,
  syncFromTemplate,
} = slice.actions;

export default slice.reducer;

export const selectPass = (state: RootState) => state.pass;
